<template>
  <div class="create-document-page">
    <!-- Main Content Area -->
    <div class="main-content">
      <!-- Left Panel - Elements Toolbox -->
      <div class="left-panel">
        <ElementsPanel 
          :disabled="isFormPreviewActive || isAiProcessing"
          @drag-start="handleElementDragStart"
        />
      </div>

      <!-- Center Panel - Form Builder -->
      <div class="center-panel">
        <!-- Action Buttons Row -->
        <div class="action-buttons-row">
          <div class="primary-actions">
            <!-- Original:
            <Button 
              colorType="sky-blue" 
              @click="handlePreview"
              :disabled="isFormPreviewActive || isAiProcessing"
            >
              <div style="display: flex; align-items: center; gap: 4px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V7H19V19ZM12 10.5C13.84 10.5 15.48 11.46 16.34 13C15.48 14.54 13.84 15.5 12 15.5C10.16 15.5 8.52 14.54 7.66 13C8.52 11.46 10.16 10.5 12 10.5ZM12 9C9.27 9 6.94 10.66 6 13C6.94 15.34 9.27 17 12 17C14.73 17 17.06 15.34 18 13C17.06 10.66 14.73 9 12 9ZM12 14.5C11.17 14.5 10.5 13.83 10.5 13C10.5 12.17 11.17 11.5 12 11.5C12.83 11.5 13.5 12.17 13.5 13C13.5 13.83 12.83 14.5 12 14.5Z" fill="white"/>
                </svg>
                Preview
              </div>
            </Button>
            -->
            <ResponsiveButton
              display_name="Preview"
              size="medium"
              button_type="filled"
              built_in_theme="light"
              :disabled="isFormPreviewActive || isAiProcessing"
              @click-button="handlePreview"
            >
              <template #prefix>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.11 3 19 3ZM19 19H5V7H19V19ZM12 10.5C13.84 10.5 15.48 11.46 16.34 13C15.48 14.54 13.84 15.5 12 15.5C10.16 15.5 8.52 14.54 7.66 13C8.52 11.46 10.16 10.5 12 10.5ZM12 9C9.27 9 6.94 10.66 6 13C6.94 15.34 9.27 17 12 17C14.73 17 17.06 15.34 18 13C17.06 10.66 14.73 9 12 9ZM12 14.5C11.17 14.5 10.5 13.83 10.5 13C10.5 12.17 11.17 11.5 12 11.5C12.83 11.5 13.5 12.17 13.5 13C13.5 13.83 12.83 14.5 12 14.5Z" fill="white"/>
                </svg>
              </template>
            </ResponsiveButton>
            <!-- Original:
            <Button 
              colorType="dark-blue" 
              @click="handleSaveForm"
              :disabled="isFormPreviewActive || isAiProcessing"
            >
              <div style="display: flex; align-items: center; gap: 4px;">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM19 19H5V5H16.17L19 7.83V19ZM12 12C10.34 12 9 13.34 9 15C9 16.66 10.34 18 12 18C13.66 18 15 16.66 15 15C15 13.34 13.66 12 12 12ZM6 6H15V10H6V6Z" fill="white"/>
                </svg>
                Save Form
              </div>
            </Button>
            -->
            <ResponsiveButton
              display_name="Save Form"
              size="medium"
              button_type="filled"
              built_in_theme="dark"
              :disabled="isFormPreviewActive || isAiProcessing"
              @click-button="handleSaveForm"
            >
              <template #prefix>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3ZM19 19H5V5H16.17L19 7.83V19ZM12 12C10.34 12 9 13.34 9 15C9 16.66 10.34 18 12 18C13.66 18 15 16.66 15 15C15 13.34 13.66 12 12 12ZM6 6H15V10H6V6Z" fill="white"/>
                </svg>
              </template>
            </ResponsiveButton>
          </div>
        </div>

        <!-- Form Builder Card -->
        <div class="form-builder-card" >
          <div :class = "['form-builder-content', { 'preview-mode': isFormPreviewActive }]">
            <!-- AI Form Preview Actions -->
            <div v-if="isFormPreviewActive" class="form-preview-actions">
              <!-- Original:
              <button class="preview-action-button discard-button" @click="handleDiscardForm">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#D10000"/>
                </svg>
                <span>Discard</span>
              </button>
              -->
              <ResponsiveButton
                display_name="Discard"
                button_type="outlined"
                built_in_theme="error"
                size="small"
                @click-button="handleDiscardForm"
              >
                <template #prefix>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#D10000"/>
                  </svg>
                </template>
              </ResponsiveButton>
              <div class="preview-label">Preview</div>
              <!-- Original:
              <button class="preview-action-button accept-button" @click="handleAcceptForm">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
                </svg>
                <span>Accept</span>
              </button>
              -->
              <ResponsiveButton
                display_name="Accept"
                button_type="filled"
                built_in_theme="dark"
                size="small"
                @click-button="handleAcceptForm"
              >
                <template #prefix>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="white"/>
                  </svg>
                </template>
              </ResponsiveButton>
            </div>

            <!-- Form Title -->
            <div class="form-title-section">
              <FormTitlePreview
                :selectedId="selectedElementId"
                :title="formTitle"
                :disabled="isFormPreviewActive || isAiProcessing"
                @update:title="formTitle = $event"
                @element-selected="handleElementSelected"
                @element-deselected="handleElementDeselected"
              />
            </div>

            <!-- Form Loading Overlay -->
            <FormLoadingOverlay v-if="isGeneratingForm" />

            <!-- Form Content Area -->
            <div class="form-content">
              <FormDropArea 
                :selectedId="selectedElementId"
                :formElements="formElements"
                :disabled="isFormPreviewActive || isAiProcessing"
                @element-dropped="handleElementDropped"
                @element-removed="handleElementRemoved"
                @element-type-changed="handleElementTypeChanged"
                @element-reordered="handleElementReordered"
                @element-data-updated="handleElementDataUpdated"
                @element-selected="handleElementSelected"
                @element-deselected="handleElementDeselected"
                @open-ai="openAiAssistant"
                @auto-mapping="handleAutoMapping"
                @element-edit-state-changed="handleElementEditStateChanged"
              />
            </div>
          </div>
          
        </div>
      </div>

      <!-- Right Panel - Mapping/AI Assistant -->
      <div class="right-panel">
        <RightSidePanel 
          ref="rightSidePanel"
          :mappableItems="mappableItems"
          :selectedId="selectedMappingItemId"
          :autoMappingLoadingMap="autoMappingLoadingMap"
          :aiChatHistory="aiChatHistory"
          :isFormPreviewActive="isFormPreviewActive || isAiProcessing"
          @ai-message="handleAiMessage"
          @update-chat-history="handleUpdateChatHistory"
          @mapping-changed="handleMappingChanged"
          @delete-element="handleMappingDeleteElement"
          @item-select="handleMappingItemSelect"
          @item-deselect="handleMappingItemDeselect"
        />
      </div>
    </div>

    <!-- Form Preview Modal -->
    <FormPreviewModal
      :formTitle="formTitle"
      :visible="isPreviewModalVisible"
      :formElements="formElements"
      @close="handleClosePreview"
      @submit="handlePreviewSubmit"
    />
    <UnsavedChangesDialog
      v-model="showUnsavedChangesDialog"
      :loading="isUnsavedChangesSaving"
      loading-text="Saving form..."
      message="You have unsaved changes in this form. Save before leaving this page?"
      @save="handleUnsavedChangesSave"
      @discard="handleUnsavedChangesDiscard"
      @cancel="handleUnsavedChangesCancel"
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
// import Button from '@/NewLibrary/Button.vue';
import UnsavedChangesDialog from '@/components/UnsavedChangesDialog.vue';
// Original: import ActionButton from '@/views/commonComponents/ActionButton.vue';
// Replaced with globally registered FoldableButton from @vibrant-wellness/va-responsive-components-library
import ElementsPanel from './components/ElementsPanel.vue';
import RightSidePanel from './components/RightSidePanel.vue';
import FormDropArea from './components/FormDropArea.vue';
import FormPreviewModal from './components/FormPreviewModal.vue';
import FormLoadingOverlay from './components/FormLoadingOverlay.vue';
import FormTitlePreview from './components/previews/FormTitlePreview.vue';
import { createFormElement, getDefaultData } from './components/formElementFactory';
import { notify } from '@/utils/notify';
import { createStableSnapshot, registerUnsavedChangesHandler, clearUnsavedChangesHandler } from '@/utils/unsavedChanges';
import { suggestMappingApi, generateFormWithAIApi } from '@/api/formBuilder/formApis';

/**
 * Create/Edit Document Page
 * 
 * Form builder interface with drag-and-drop elements, live preview, and mapping panel.
 * Uses Vuex store for business state (form data, elements), keeps only UI state local.
 * 
 * Architecture:
 * - Business state (form title, elements, selections) → Vuex store
 * - UI state (editing flags, drag state) → Local component state
 * - Element factory handles element creation with proper defaults
 */
export default {
  name: 'CreateDocument',
  components: {
    // Button, // replaced by globally registered ResponsiveButton
    UnsavedChangesDialog,
    // ActionButton, // replaced by globally registered FoldableButton
    ElementsPanel,
    RightSidePanel,
    FormDropArea,
    FormPreviewModal,
    FormLoadingOverlay,
    FormTitlePreview
  },
  beforeRouteLeave(to, from, next) {
    this.handleRouteGuard(next);
  },
  beforeRouteUpdate(to, from, next) {
    this.handleRouteGuard(next);
  },
  
  data() {
    return {

      formTitle: "",
      // ═══════════════════════════════════════════════════════════
      // UI STATE (Local) - UI interactions and temporary states
      // ═══════════════════════════════════════════════════════════
      
      /** Currently dragging element (temporary during drag operation) */
      currentDragElement: null,
      
      /** Flag for preview modal visibility */
      isPreviewModalVisible: false,
      
      /** Loading state for auto-mapping by element ID */
      autoMappingLoadingMap: {},
      savedFormSnapshot: '',
      showUnsavedChangesDialog: false,
      isUnsavedChangesSaving: false,
      pendingNavigationResolver: null,
      isBypassingUnsavedChangesGuard: false,
      skipNextRouteGuard: false,
      beforeUnloadHandler: null,
      unsavedChangesHandler: null,
      
      // AI Assistant state
      aiChatHistory: [],
      isFormPreviewActive: false,
      isAiProcessing: false,
      isGeneratingForm: false,
      generatedFormPreview: null,
      previousFormElements: [],
      previousFormTitle: '',
    };
  },
  computed: {
    // ─────────────────────────────────────────────────────────
    // Map Vuex Store State
    // ─────────────────────────────────────────────────────────
    ...mapState('workbench/formBuilder', {
      currentFormTitle: 'currentFormTitle',
      formElements: 'formElements',
      selectedElementId: 'selectedElementId',
      selectedMappingItemId: 'selectedMappingItemId',
      isSavingForm: 'isSavingForm',
      formEditorError: 'formEditorError',
    }),
    
    // ─────────────────────────────────────────────────────────
    // Map Vuex Store Getters
    // ─────────────────────────────────────────────────────────
    ...mapGetters('workbench/formBuilder', {
      mappableItems: 'mappableElements',
    }),
    hasUnsavedChanges() {
      return this.savedFormSnapshot !== this.buildFormSnapshot();
    },
  },
  watch: {
    currentFormTitle(newVal) {
      this.formTitle = newVal;
    },
    formTitle(newVal) {
      // Sync local formTitle changes to Vuex store
      if (newVal !== this.currentFormTitle) {
        this.$store.commit('workbench/formBuilder/SET_CURRENT_FORM_TITLE', newVal);
      }
    },
    // formElements(newVal) {
    //   console.log('formElements changed:', newVal);
    // }
  },
  created() {
    this.formTitle = this.currentFormTitle;
  },
  async mounted() {
    // Initialize form editor (load existing form if editing)
    const formId = this.$route.params.id || null;
    await this.initFormEditor(formId);
    
    // Load mappable variables for mapping panel
    await this.loadMappableVariables();

    this.updateSavedFormSnapshot();
    this.beforeUnloadHandler = (event) => {
      if (!this.hasUnsavedChanges || this.isBypassingUnsavedChangesGuard) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    this.unsavedChangesHandler = async () => {
      if (this.isBypassingUnsavedChangesGuard || !this.hasUnsavedChanges) {
        return true;
      }

      return await new Promise((resolve) => {
        this.queueNavigationAction((shouldProceed) => {
          if (shouldProceed === false) {
            resolve(false);
            return;
          }

          this.skipNextRouteGuard = true;
          resolve(true);
        }, { isRouteGuard: true });
      });
    };
    registerUnsavedChangesHandler(this.unsavedChangesHandler);
  },
  beforeUnmount() {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
    }
    clearUnsavedChangesHandler(this.unsavedChangesHandler);
  },
  methods: {
    // ─────────────────────────────────────────────────────────
    // Map Vuex Store Actions
    // ─────────────────────────────────────────────────────────
    ...mapActions('workbench/formBuilder', [
      'initFormEditor',
      'loadMappableVariables',
      'addElement',
      'removeElement',
      'removeElementById',
      'updateElementEditingState',
      'updateElementData',
      'updateElementMapping',
      'reorderElements',
      'selectElement',
      'deselectElement',
      'saveForm',
    ]),
    buildFormSnapshot() {
      return createStableSnapshot({
        title: this.currentFormTitle,
        elements: this.formElements,
      });
    },
    updateSavedFormSnapshot() {
      this.savedFormSnapshot = this.buildFormSnapshot();
    },
    queueNavigationAction(action, options = {}) {
      if (this.showUnsavedChangesDialog || this.pendingNavigationResolver) {
        return;
      }

      this.pendingNavigationResolver = {
        action,
        isRouteGuard: Boolean(options.isRouteGuard),
      };
      this.showUnsavedChangesDialog = true;
    },
    handleRouteGuard(next) {
      if (this.unsavedChangesHandler) {
        if (this.skipNextRouteGuard) {
          this.skipNextRouteGuard = false;
        }
        next();
        return;
      }

      if (this.skipNextRouteGuard) {
        this.skipNextRouteGuard = false;
        next();
        return;
      }

      if (this.isBypassingUnsavedChangesGuard || !this.hasUnsavedChanges) {
        next();
        return;
      }

      this.queueNavigationAction(next, { isRouteGuard: true });
    },
    resolvePendingNavigation(shouldProceed) {
      const pendingNavigation = this.pendingNavigationResolver;
      this.pendingNavigationResolver = null;
      this.showUnsavedChangesDialog = false;

      if (!pendingNavigation) {
        return;
      }

      const { action, isRouteGuard } = pendingNavigation;

      if (!shouldProceed) {
        if (isRouteGuard) {
          action(false);
        }
        return;
      }

      this.isBypassingUnsavedChangesGuard = true;
      action();
      this.$nextTick(() => {
        this.isBypassingUnsavedChangesGuard = false;
      });
    },
    async handleUnsavedChangesSave() {
      this.isUnsavedChangesSaving = true;

      try {
        const wasSaved = await this.handleSaveForm({ notifyOnSuccess: true });

        if (!wasSaved) {
          return;
        }

        this.resolvePendingNavigation(true);
      } finally {
        this.isUnsavedChangesSaving = false;
      }
    },
    handleUnsavedChangesDiscard() {
      this.resolvePendingNavigation(true);
    },
    handleUnsavedChangesCancel() {
      this.resolvePendingNavigation(false);
    },
    
    // ─────────────────────────────────────────────────────────
    // Navigation Handlers
    // ─────────────────────────────────────────────────────────
    
    /**
     * Navigate back to home page
     */
    handleBack() {
      this.$router.push('/my-workbench/document-template');
    },
    
    // ─────────────────────────────────────────────────────────
    // Element Drag & Drop Handlers
    // ─────────────────────────────────────────────────────────
    
    /**
     * Handle element drag start (store dragged element temporarily)
     */
    handleElementDragStart(element) {
      this.currentDragElement = element;
    },
    
    /**
     * Handle element dropped into form (create new element instance)
     */
    handleElementDropped(payload) {
      const { element, index } = payload;
      const newElement = createFormElement(element);
      
      // Add to store
      this.addElement({ element: newElement, index });
      
      // Clear drag state
      this.currentDragElement = null;
    },
    
    /**
     * Handle element removed from form
     */
    handleElementRemoved(index) {
      this.removeElement(index);
    },

    /**
     * Handle element edit state changed
     */
    handleElementEditStateChanged(payload) {
      const { id, isEditing } = payload;
      // Update store
      this.updateElementEditingState({ id, isEditing });
    },
    
    /**
     * Handle element type changed (preserve id, reset data)
     */
    handleElementTypeChanged(payload) {
      const { index, newElement } = payload;
      const elementId = this.formElements[index].id;
      
      // Create new element with fresh defaults but same id
      const updatedElement = {
        type: newElement.type,
        label: newElement.label,
        id: elementId,
        data: getDefaultData(newElement.type)
      };
      
      // Update in store
      this.$store.commit('workbench/formBuilder/UPDATE_FORM_ELEMENT', { index, element: updatedElement });
    },
    
    /**
     * Handle element reordered (drag to reposition)
     */
    handleElementReordered({ fromIndex, toIndex }) {
      this.reorderElements({ fromIndex, toIndex });
    },
    
    /**
     * Handle element data updated (field properties changed)
     */
    handleElementDataUpdated({ index, data }) {
      this.updateElementData({ index, data });
    },
    
    // ─────────────────────────────────────────────────────────
    // Mapping Panel Handlers
    // ─────────────────────────────────────────────────────────
    
    /**
     * Handle mapping changed for an element
     */
    handleMappingChanged({ id, mapping }) {
      this.updateElementMapping({ id, mapping });
    },
    
    /**
     * Handle delete element from mapping panel
     */
    handleMappingDeleteElement(id) {
      this.removeElementById(id);
    },
    
    /**
     * Handle mapping item selected (sync with preview)
     */
    handleMappingItemSelect(item) {
      this.selectElement(item.id);
    },
    
    /**
     * Handle mapping item deselected
     */
    handleMappingItemDeselect(item) {
      if (this.selectedElementId === item.id) {
        this.deselectElement();
      }
    },
    
    // ─────────────────────────────────────────────────────────
    // Preview Element Selection Handlers
    // ─────────────────────────────────────────────────────────
    
    /**
     * Handle element selected in preview (sync with mapping panel)
     */
    handleElementSelected(id) {
      this.selectElement(id);
    },
    
    /**
     * Handle element deselected in preview
     */
    handleElementDeselected(id) {
      if (this.selectedElementId === id) {
        this.deselectElement();
      }
    },
    
    // ─────────────────────────────────────────────────────────
    // Auto-Mapping Handler
    // ─────────────────────────────────────────────────────────
    
    /**
     * Handle auto-mapping request (AI-suggested mapping)
     * Calls POST /ai/suggest-mappings with full form context
     */
    async handleAutoMapping(id) {
      console.log('Auto-mapping requested for:', id);
      
      // Prevent duplicate calls for the same element
      if (this.autoMappingLoadingMap[id]) {
        console.log('Auto-mapping already in progress for:', id);
        return;
      }
      
      // Find the element to get its type
      const element = this.formElements.find(el => el.id === id);
      if (!element) {
        console.error('Element not found for auto-mapping:', id);
        return;
      }
      
      try {
        // Set loading state for this element
        this.autoMappingLoadingMap[id] = true;
        
        // Call AI mapping suggestion API with full form context
        const response = await suggestMappingApi({
          name: this.formTitle,
          definition: {
            fields: this.formElements
          },
          field_id: id
        });
        
        console.log('AI mapping suggestion response:', response);
        
        // Handle non-mappable field type
        if (response.suggestion === null) {
          notify({
            title: 'Not Mappable',
            message: `This field type (${element.type}) cannot be mapped to patient variables.`,
            type: 'info',
            duration: 3000
          });
          return;
        }
        
        const { mapping, confidence, rationale } = response.suggestion;
        
        // Handle no suitable match
        if (!mapping || confidence === 0) {
          notify({
            title: 'No Mapping Found',
            message: rationale || 'No suitable patient variable matches this field.',
            type: 'warning',
            duration: 4000
          });
          return;
        }
        
        // Validate that the suggested mapping is in this element's available options
        const availableOptions = this.$store.getters['workbench/formBuilder/getMappingOptionsForType'](element.type, id);
        const isValidMapping = availableOptions.some(option => option.key === mapping);
        
        if (isValidMapping) {
          // Valid mapping - apply it
          this.updateElementMapping({ 
            id, 
            mapping 
          });
          
          notify({
            title: 'Mapping Applied',
            message: `AI suggested: ${mapping} (Confidence: ${Math.round(confidence * 100)}%)${rationale ? '\n' + rationale : ''}`,
            type: 'success',
            duration: 5000
          });
        } else {
          // Invalid mapping (already used by another field)
          notify({
            title: 'Mapping Unavailable',
            message: `AI suggested "${mapping}" but it's already mapped to another field.`,
            type: 'warning',
            duration: 4000
          });
        }
      } catch (error) {
        console.error('Error getting AI mapping suggestion:', error);
        notify({
          title: 'Error',
          message: 'Failed to get AI mapping suggestion. Please try again.',
          type: 'error',
          duration: 3000
        });
      } finally {
        // Clear loading state for this element
        this.autoMappingLoadingMap[id] = false;
      }
    },
    
    // ─────────────────────────────────────────────────────────
    // Form Actions (Save, Preview, AI)
    // ─────────────────────────────────────────────────────────
    
    /**
     * Handle preview button click - opens preview modal
     */
    handlePreview() {
      this.isPreviewModalVisible = true;
    },
    
    /**
     * Handle closing preview modal
     */
    handleClosePreview() {
      this.isPreviewModalVisible = false;
    },
    
    /**
     * Handle form submission from preview modal
     */
    handlePreviewSubmit(formData) {
      console.log('Form preview submitted with data:', formData);
      // Close modal after submission
      this.isPreviewModalVisible = false;
      // Show success message
      notify({
        type: 'success',
        message: 'Form preview submitted successfully!'
      });
    },
    
    /**
     * Handle AI message from user
     */
    async handleAiMessage(message) {
      try {
        // Show loading overlay
        this.isAiProcessing = true;
        
        // Call AI API to generate form
        const response = await generateFormWithAIApi({
          description: message,
          definition: this.formElements.length > 0 ? { fields: this.formElements } : undefined
        });
        
        // Extract message from response
        const aiMessage = response?.message || 'I\'ve generated the form based on your request. Please review it below.';
        
        // Add AI response to chat
        this.$refs.rightSidePanel?.addAiResponse(aiMessage);
        
        // Check if form definition exists with fields
        if (response.rejected === false) {
          this.isGeneratingForm = true;
          // Store the current form elements and title before replacing
          this.previousFormElements = JSON.parse(JSON.stringify(this.formElements));
          this.previousFormTitle = this.formTitle;

          // Wait for animation to complete (minimum 2s for all 3 skeleton items)
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Update form title if provided in response
          if (response?.name) {
            this.formTitle = response.name;
          }

          if(response?.definition?.fields && response.definition.fields.length > 0){
            // Store the generated form preview
            this.generatedFormPreview = response.definition;
            
            // Replace all form elements with AI-generated ones
            this.$store.commit('workbench/formBuilder/SET_FORM_ELEMENTS', response.definition.fields);
            // Hide generating form overlay
            
          }
          this.isGeneratingForm = false;
 
          // Hide loading overlay
          this.isAiProcessing = false;
          
          // Show form preview overlay
          this.isFormPreviewActive = true;
        } else {
          // No form generated, just hide loading
          this.isAiProcessing = false;
        }
      } catch (error) {
        console.error('Error generating form with AI:', error);
        
        // Hide loading overlay on error
        this.isAiProcessing = false;
        
        const errorMessage = error?.message || 'Failed to generate form. Please try again.';
        this.$refs.rightSidePanel?.addAiResponse(errorMessage, true);
      }
    },
    
    /**
     * Handle chat history update from RightSidePanel
     */
    handleUpdateChatHistory(history) {
      this.aiChatHistory = history;
    },
    
    /**
     * Handle opening AI assistant (from form element)
     */
    openAiAssistant() {
      // Switch to AI tab in right panel
      if (this.$refs.rightSidePanel) {
        this.$refs.rightSidePanel.activeTab = 'ai';
      }
    },
    
    /**
     * Handle accepting AI-generated form
     */
    handleAcceptForm() {
      // Form is already applied (including AI-generated title), just clear preview state
      this.isFormPreviewActive = false;
      this.generatedFormPreview = null;
      this.previousFormElements = [];
      this.previousFormTitle = '';
      
      // Add success message to chat
      this.$refs.rightSidePanel?.addAiResponse('Form accepted! The changes have been applied.');
      
      // Show notification
      notify({
        title: 'Form Accepted',
        message: 'AI-generated form has been applied successfully.',
        type: 'success',
        duration: 3000
      });
    },
    
    /**
     * Handle discarding AI-generated form
     */
    handleDiscardForm() {
      // Restore previous form elements and title
      this.$store.commit('workbench/formBuilder/SET_FORM_ELEMENTS', this.previousFormElements);
      this.formTitle = this.previousFormTitle;
      
      // Clear preview state
      this.isFormPreviewActive = false;
      this.generatedFormPreview = null;
      this.previousFormElements = [];
      this.previousFormTitle = '';
      
      // Add message to chat
      this.$refs.rightSidePanel?.addAiResponse('Form discarded. Feel free to try another request.');
    },
    
    /**
     * Handle save form button click
     */
    async handleSaveForm(options = {}) {
      const { notifyOnSuccess = true } = options;

      try{
        const result = await this.saveForm();
      
        if (result.success) {
          console.log('Form saved successfully');
          this.updateSavedFormSnapshot();
          if (notifyOnSuccess) {
            notify({
              title: 'Form saved successfully',
              message: 'Form saved successfully',
              type: 'success',
              duration: 4000
            });
          }
          return true;
        } else {
          console.error('Failed to save form:', result.error);
          notify({
            title: 'Failed to save form',
            message: 'Failed to save form',
            type: 'error',
            duration: 4000
          });
          return false;
        }
      } catch (error) {
        console.error('Failed to save form:', error);
        notify({
          title: 'Failed to save form',
          message: 'Failed to save form',
          type: 'error',
          duration: 4000
        });
        return false;
      }   
    }
  }
};
</script>

<style scoped>
.create-document-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - var(--nav-height));
  background: #F5F5F5;
  overflow: hidden;
}

.page-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: #fff;
  border-bottom: 1px solid #E6E6E6;
  flex-shrink: 0;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: transparent;
  border: none;
  color: #333;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: rgba(0, 72, 121, 0.08);
  color: #004879;
}

.back-btn svg {
  width: 24px;
  height: 24px;
}

.main-content {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.left-panel {
  width: 346px;
  min-width: 346px;
  max-width: 346px;
  overflow-y: auto;
  flex-shrink: 0;
}

.center-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 24px 24px;
  min-width: 0;
  overflow: hidden;
  position: relative;
}

.right-panel {
  width: 346px;
  min-width: 346px;
  max-width: 346px;
  overflow-y: auto;
  flex-shrink: 0;
}

.action-buttons-row {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 4px 0;
  flex-shrink: 0;
}

.primary-actions {
  display: flex;
  gap: 8px;
}
.form-builder-card{
  flex: 1;
  padding: 16px 24px 24px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
  min-height: 0;
}
.form-builder-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.form-builder-content.preview-mode {
  padding: 16px;
  border: 2px solid #7CD4DA;
  border-radius: 10px;
}

.form-title-section {
  border-bottom: 1px dashed #C4C4C4;
  padding: 4px 6px;
  flex-shrink: 0;
  margin-bottom: 4px;
}

.form-title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 6px;
}

.form-title {
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  margin: 0;
}

.form-title-edit-box {
  width: 100%;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  padding: 3px 4px;
  gap: 4px;
  min-width: 240px;
  height: 32px;
}

.form-title-edit-input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-family: 'Avenir', sans-serif;
  font-size: 22px;
  font-weight: 500;
  line-height: 24px;
  color: #333;
  min-width: 0;
}

.form-title-save-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0 8px;
  white-space: nowrap;
  flex-shrink: 0;
}

.form-title-save-btn span {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: #1E4670;
  text-decoration: underline;
}

.edit-title-btn {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #666;
  transition: all 0.2s ease;
}

.edit-title-btn:hover {
  background: rgba(0, 72, 121, 0.08);
  color: #004879;
}

.form-preview-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex-shrink: 0;
  margin-bottom: 16px;
}

.preview-action-button {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 32px;
  padding: 4px 32px;
  border: none;
  border-radius: 4px;
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
}

.preview-action-button svg {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.discard-button {
  background: #FFFFFF;
  border: 1px solid #D10000;
  color: #D10000;
}

.discard-button:hover {
  background: #FFF5F5;
}

.accept-button {
  background: #004879;
  color: #FFFFFF;
}

.accept-button:hover {
  background: #20A7C1;
}

.preview-label {
  background: #F9FBFD;
  padding: 5px 24px;
  border-radius: 5px;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  color: #666;
  text-align: center;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  position: relative;
  transition: opacity 0.2s ease;
}

.form-content.preview-active {
  pointer-events: none;
  opacity: 0.9;
}

/* Responsive Design */
@media (max-width: 1400px) {
  .right-panel {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
  }
}

@media (max-width: 1200px) {
  .left-panel {
    width: 24%;
    min-width: 24%;
    max-width: 24%;
  }
  
  .right-panel {
    width: 280px;
    min-width: 280px;
    max-width: 280px;
  }
}

@media (max-width: 992px) {
  .main-content {
    flex-direction: column;
  }
  
  .left-panel,
  .right-panel {
    width: 100%;
    min-width: 100%;
    max-width: 100%;
    max-height: 300px;
  }
  
  .right-panel {
    order: 3;
  }
  
  .center-panel {
    order: 2;
  }
  
  .left-panel {
    order: 1;
  }
}

@media (max-width: 768px) {
  .action-buttons-row {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }
  
  .primary-actions {
    justify-content: flex-end;
  }
  
  .page-header {
    padding: 12px 16px;
  }
  
  .center-panel {
    padding: 12px 16px;
  }
}

@media (max-width: 576px) {
  .primary-actions {
    flex-direction: column;
  }
  
  .form-title {
    font-size: 18px;
  }
  
  .form-title-input {
    font-size: 18px;
  }
}
</style>
